import React, { useEffect, useMemo, useState } from 'react';
import { getDownloadURL, ref } from 'firebase/storage';
import EmbeddedVideoCard from '../../components/domain/EmbeddedVideoCard';
import ResourceRow, { resourceType } from '../../components/domain/ResourceRow';
import { EmptyState, InlineAlert, LoadingState, PageHeader, SearchField } from '../../components/ui/Primitives';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { storage } from '../../firebase';
import { millionairesConnectLessonResources } from '../../data/economyPrograms';
import { loadMillionairesConnectProgress, persistMillionairesConnectProgress } from '../../utils/courseProgress';
import { isSequentialLessonUnlocked } from '../../utils/lessonSequence';
import { isMillionairesConnectCourse, resourceMatchesActiveCourse } from '../../utils/resourceAccess';
import { hasSeasonSixAccess, isSeasonSixResource } from '../../utils/seasonSixAccess';

export default function StudentResources() {
  const { user } = useAuth();
  const { resources = [], courses = [], loading = false } = useData() || {};
  const [query, setQuery] = useState('');
  const [type, setType] = useState('All types');
  const [opening, setOpening] = useState('');
  const [error, setError] = useState('');
  const [completedLessonIds, setCompletedLessonIds] = useState(() => new Set());
  const [activeVideo, setActiveVideo] = useState(null);
  const millionairesConnectEnrolled = isMillionairesConnectCourse(courses, user?.courseId, user?.intake);
  const millionairesConnectAccess = millionairesConnectEnrolled && hasSeasonSixAccess(user);
  const publishedResources = useMemo(() => resources.filter(resource => resourceMatchesActiveCourse({
    resource, courses, activeCourseId: user?.courseId, activeIntake: user?.intake
  })), [resources, courses, user?.courseId, user?.intake]);
  const authorised = useMemo(() => millionairesConnectEnrolled
    ? [...publishedResources, ...millionairesConnectLessonResources]
    : publishedResources, [millionairesConnectEnrolled, publishedResources]);

  useEffect(() => {
    let cancelled = false;
    if (!millionairesConnectEnrolled) {
      setCompletedLessonIds(new Set());
      return undefined;
    }
    loadMillionairesConnectProgress(user?.id).then((ids) => {
      if (!cancelled) setCompletedLessonIds(new Set(ids));
    });
    return () => { cancelled = true; };
  }, [millionairesConnectEnrolled, user?.id]);

  const types = useMemo(() => Array.from(new Set(authorised.map(resourceType))).sort(), [authorised]);
  const visible = authorised.filter(resource => {
    const text = [resource.title, resource.description, resource.module, resource.group, resource.folder].filter(Boolean).join(' ').toLowerCase();
    return (type === 'All types' || resourceType(resource) === type) && text.includes(query.trim().toLowerCase());
  });
  const isLocked = resource => {
    if (resource.isMillionairesConnectVideo) {
      return !isSequentialLessonUnlocked({
        lessons: millionairesConnectLessonResources,
        lessonIndex: resource.sequenceIndex,
        completedLessonIds,
        hasAccess: millionairesConnectAccess
      });
    }
    return (resource.isPaid && !user?.lmsActive) || (isSeasonSixResource(resource) && !hasSeasonSixAccess(user));
  };
  const openResource = async resource => {
    setError('');
    if (isLocked(resource)) { setError('This resource is locked. Open Classroom to use the existing access flow.'); return; }
    setOpening(resource.id);
    try {
      const url = resource.embedUrl
        || resource.downloadUrl
        || resource.url
        || (resource.storagePath ? await getDownloadURL(ref(storage, resource.storagePath)) : '');
      if (!url) throw new Error('Resource URL unavailable');
      if (resourceType(resource) === 'Video') {
        setActiveVideo({ resource, sourceUrl: url });
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (openError) {
      console.error('Failed to open resource', openError);
      setError('This resource could not be opened. Please try again.');
    } finally { setOpening(''); }
  };

  const completeLesson = resource => {
    const next = new Set(completedLessonIds);
    next.add(resource.lessonId);
    setCompletedLessonIds(next);
    persistMillionairesConnectProgress(user?.id, [...next]);
  };

  return (
    <div>
      <PageHeader eyebrow="Library" title="Resources" description="Course materials and supporting documents." />
      <div className="resource-toolbar">
        <SearchField value={query} onChange={event => setQuery(event.target.value)} placeholder="Search resources" label="Search resources" />
        <div><select aria-label="Filter by resource type" value={type} onChange={event => setType(event.target.value)}><option>All types</option>{types.map(item => <option key={item}>{item}</option>)}</select></div>
      </div>
      {error && <InlineAlert tone="danger">{error}</InlineAlert>}
      {loading ? <LoadingState label="Loading resources…" /> : visible.length ? <div>{visible.map(resource => (
        <React.Fragment key={resource.id}>
          <ResourceRow resource={resource} locked={isLocked(resource)} busy={opening === resource.id} onOpen={openResource} actionLabel={resourceType(resource) === 'Video' ? (completedLessonIds.has(resource.lessonId) ? 'Rewatch here →' : 'Watch here →') : undefined} />
          {activeVideo?.resource?.id === resource.id && (
            <EmbeddedVideoCard resource={activeVideo.resource} sourceUrl={activeVideo.sourceUrl} lessons={millionairesConnectLessonResources} completedLessonIds={completedLessonIds} hasAccess={millionairesConnectAccess} onComplete={completeLesson} onSelectLesson={(lesson) => setActiveVideo({ resource: lesson, sourceUrl: lesson.embedUrl })} onClose={() => setActiveVideo(null)} />
          )}
        </React.Fragment>
      ))}</div> : <EmptyState title={authorised.length ? 'No resources match your search' : 'No resources have been published'} description={authorised.length ? 'Try another search term or file type.' : 'Course resources will appear here when they are ready.'} />}
    </div>
  );
}
